import type {
    ElementInsertionOption,
    ElementInsertionPresetProvider,
    ElementInsertionPresetOption,
    ElementInsertionPresetResolver,
    ElementInsertionSlotKind,
    RenderContextKind,
    ReportElementConstructor,
} from './types';
import { elementInsertionPresetProviders } from '../runtime/dataServices/insertionPresetProviders';
import { validateElementLayoutMetadata } from './elementLayout';
import { assertSupportedSwarmTypes } from '../swarmCompatibility';

export function createElementClassRegistry<TElementClass extends ReportElementConstructor>(
    elementClasses: readonly TElementClass[]
): ReadonlyMap<TElementClass['elementType'], TElementClass> {
    const registry = new Map<TElementClass['elementType'], TElementClass>();

    elementClasses.forEach((ElementClass) => {
        if (registry.has(ElementClass.elementType)) {
            throw new Error(`Duplicate element type "${ElementClass.elementType}" in registry.`);
        }

        validateElementLayoutMetadata(ElementClass);
        registry.set(ElementClass.elementType, ElementClass);
    });

    return registry;
}

export function getElementClassForType(
    type: string,
    elementClassRegistry: ReadonlyMap<string, ReportElementConstructor>
): ReportElementConstructor {
    const elementClass = elementClassRegistry.get(type);

    if (!elementClass) {
        throw new Error(`Unsupported element type "${type}".`);
    }

    return elementClass;
}

function createInsertionPresetOptions<TElementType extends string>(
    ElementClass: ReportElementConstructor<TElementType>,
    contextKind: RenderContextKind
): readonly ElementInsertionPresetOption[] {
    const insertionPresets = ElementClass.insertion.presets;
    if (typeof insertionPresets === 'undefined') {
        return [];
    }

    if (!insertionPresets.length) {
        throw new Error(
            `Element "${ElementClass.elementType}" defines insertion presets but none were provided.`
        );
    }

    const presetKeys = new Set<string>();

    return insertionPresets
        .filter((preset) => preset.compatibleContextKinds.includes(contextKind))
        .map((preset, index) => {
            const trimmedPresetKey = preset.key.trim();
            if (!trimmedPresetKey.length) {
                throw new Error(
                    `Element "${ElementClass.elementType}" insertion preset at index ${index} has an empty key.`
                );
            }

            if (presetKeys.has(trimmedPresetKey)) {
                throw new Error(
                    `Element "${ElementClass.elementType}" defines duplicate insertion preset key "${trimmedPresetKey}".`
                );
            }

            presetKeys.add(trimmedPresetKey);

            if (!preset.compatibleContextKinds.length) {
                throw new Error(
                    `Element "${ElementClass.elementType}" insertion preset "${trimmedPresetKey}" must declare at least one compatible context kind.`
                );
            }

            preset.compatibleContextKinds.forEach((presetContextKind) => {
                if (!ElementClass.compatibleContextKinds.includes(presetContextKind)) {
                    throw new Error(
                        `Element "${ElementClass.elementType}" insertion preset "${trimmedPresetKey}" declares incompatible context kind "${presetContextKind}".`
                    );
                }
            });
            assertSupportedSwarmTypes(
                preset.supportedSwarmTypes,
                `${ElementClass.elementType}.insertion.presets[${index}].supportedSwarmTypes`
            );

            return {
                key: trimmedPresetKey,
                label: preset.label,
                description: preset.description,
                sourceLabel: preset.sourceLabel,
                supportedSwarmTypes: preset.supportedSwarmTypes,
                isEnabled: true,
                createDefinition: preset.createDefinition,
            };
        });
}

function getPresetProvidersForElementType(
    elementType: string,
    contextKind: RenderContextKind
): readonly ElementInsertionPresetProvider[] {
    return elementInsertionPresetProviders.filter((provider) =>
        provider.supports({ elementType, contextKind })
    );
}

function assertUniquePresetOptionKeys(
    elementType: string,
    presets: readonly ElementInsertionPresetOption[]
): void {
    const presetKeys = new Set<string>();

    presets.forEach((preset) => {
        if (presetKeys.has(preset.key)) {
            throw new Error(
                `Element "${elementType}" resolved duplicate insertion preset key "${preset.key}".`
            );
        }

        presetKeys.add(preset.key);
    });
}

function assertResolvedPresetContracts(
    elementType: string,
    presets: readonly ElementInsertionPresetOption[]
): void {
    presets.forEach((preset) => {
        assertSupportedSwarmTypes(
            preset.supportedSwarmTypes,
            `${elementType}.resolvedPreset.${preset.key}.supportedSwarmTypes`
        );
    });
}

function createInsertionPresetResolver<TElementType extends string>(
    ElementClass: ReportElementConstructor<TElementType>,
    contextKind: RenderContextKind,
    staticPresets: readonly ElementInsertionPresetOption[],
    presetProviders: readonly ElementInsertionPresetProvider[]
): ElementInsertionPresetResolver {
    return async ({ client, context }) => {
        if (context.kind !== contextKind) {
            throw new Error(
                `Insertion preset context mismatch: expected "${contextKind}", received "${context.kind}".`
            );
        }

        const providerPresets = (
            await Promise.all(
                presetProviders.map(async (provider) => {
                    const resolvedPresets = await provider.resolvePresets({
                        client,
                        context,
                        elementType: ElementClass.elementType,
                    });
                    assertResolvedPresetContracts(ElementClass.elementType, resolvedPresets);
                    return resolvedPresets;
                })
            )
        ).flat();
        const presets = [...staticPresets, ...providerPresets];
        assertUniquePresetOptionKeys(ElementClass.elementType, presets);

        return presets;
    };
}

export function getElementInsertionOptionsForSlotFromRegistry<TElementType extends string>(
    contextKind: RenderContextKind,
    slotKind: ElementInsertionSlotKind,
    elementClassRegistry: ReadonlyMap<TElementType, ReportElementConstructor<TElementType>>
): ElementInsertionOption[] {
    const insertableElementClasses = Array.from(elementClassRegistry.values()).filter(
        (ElementClass) =>
            ElementClass.insertion.hiddenFromMenu !== true &&
            ElementClass.insertion.supportedSlots.includes(slotKind) &&
            ElementClass.compatibleContextKinds.includes(contextKind)
    );

    if (!insertableElementClasses.length) {
        throw new Error(`No insertable elements available for slot "${slotKind}".`);
    }

    return insertableElementClasses.map((ElementClass) => {
        const staticPresets = createInsertionPresetOptions(ElementClass, contextKind);
        const presetProviders = getPresetProvidersForElementType(
            ElementClass.elementType,
            contextKind
        );

        return {
            hasPresetSelector: staticPresets.length > 0 || presetProviders.length > 0,
            type: ElementClass.elementType,
            label: ElementClass.insertion.label,
            isEnabled: true,
            createDefinition: ElementClass.insertion.createDefinition,
            resolvePresets: createInsertionPresetResolver(
                ElementClass,
                contextKind,
                staticPresets,
                presetProviders
            ),
        };
    });
}
